import { useContext, useState } from "react";
import { FieldArrayWithId } from "react-hook-form";
import { Box, Button, IconButton, Stack, TableCell, TableRow, Typography } from "@mui/material";
import TextFieldControl from "components/form/TextFieldControl";
import { gradeColor } from "helpers/index";
import { Link, useParams } from "react-router-dom";
import ROUTES from "constants/routes";
import GenerateQuestionsModal from "components/modals/GenerateQuestionsModal";
import VALIDATION from "helpers/validation";
import { REDIRECT_COMPETENCY_MAP_ID_PARAM_NAME } from "constants/global";
import { Delete } from "@mui/icons-material";
import { ConfirmModalContext } from "context/confirmModalContext";
import {
  CompetencyMapInputs,
  isKnowledgeBlockCompetencyFieldsNonNullable,
  isSkillFieldsNonNullable,
  KnowledgeBlockCompetencyOption,
  SkillLiteralType,
  SkillsLiteralType,
} from "../types";
import { ConstructorTabContext } from "../../context";

interface SkillProps {
  name: SkillLiteralType;
  isEditable: boolean;
  skill: FieldArrayWithId<CompetencyMapInputs, SkillsLiteralType, "skillId">;
  knowledgeBlock: KnowledgeBlockCompetencyOption;
  remove: () => void;
}

const Skill = ({ name, isEditable, skill, knowledgeBlock, remove }: SkillProps) => {
  const [openGenerateQuestionsModal, setOpenGenerateQuestionsModal] = useState<boolean>(false);
  const isNewSkill = !skill.id;
  const { mapId } = useParams();
  const { handleOpen } = useContext(ConfirmModalContext);
  const { setSkillsIdsToDelete, refetchCompetencyMapGenQuestionsLog } = useContext(ConstructorTabContext);

  const getSkillLinkPath = () => {
    const pathname = `/${ROUTES.SKILL}/view/${skill.id}`;

    if (mapId) return `${pathname}?${REDIRECT_COMPETENCY_MAP_ID_PARAM_NAME}=${mapId}`;

    return pathname;
  };

  const handleRemoveSkill = () => {
    handleOpen({
      title: "Вы уверены, что хотите удалить навык из блока знаний?",
      handleClick: () => {
        remove();

        const skillId = skill.id;
        if (skillId) {
          setSkillsIdsToDelete((prev) => [...prev, skillId]);
        }
      },
    });
  };

  return (
    <TableRow
      sx={{
        "&:last-child td, &:last-child th": { border: 0 },
        "& th, & td": { borderLeft: "none", borderRight: "none" },
      }}>
      <TableCell
        component='th'
        scope='row'>
        {isNewSkill ? (
          <TextFieldControl
            placeholder='Название навыка'
            name={`${name}.title`}
            disabled={!isEditable}
            rules={{ required: true, ...VALIDATION.competencyMapTitle }}
            color='secondary'
            sx={{ width: 200 }}
          />
        ) : (
          <Button
            size='small'
            component={Link}
            to={getSkillLinkPath()}
            sx={{ paddingLeft: 0, color: "blue", textDecoration: "underline", justifyContent: "flex-start" }}>
            {skill.title}
          </Button>
        )}
      </TableCell>
      <TableCell>
        {skill.grades?.length ? (
          skill.grades?.map((grade) => (
            <Typography
              key={`${skill.skillId}-${grade}`}
              sx={{ color: `${gradeColor(grade)}.contrast` }}>
              {grade}
            </Typography>
          ))
        ) : (
          <Typography sx={{ color: "primary.main" }}>—</Typography>
        )}
      </TableCell>
      <TableCell>
        <Stack
          direction='row'
          sx={{ justifyContent: "space-between" }}>
          <Box>
            <Typography
              component='span'
              sx={{ color: !skill.confirmedQuestionsCount ? "error.contrastText" : "primary.main" }}>
              {skill.confirmedQuestionsCount || 0}
            </Typography>
            <Typography
              component='span'
              sx={{ color: "primary.main" }}>
              /{skill.notConfirmedQuestionsCount || 0}
            </Typography>
          </Box>
          {!isNewSkill && knowledgeBlock.id && (
            <Button
              size='small'
              onClick={() => setOpenGenerateQuestionsModal(true)}>
              Сгенерировать
            </Button>
          )}
        </Stack>
      </TableCell>
      <TableCell>
        <IconButton
          onClick={handleRemoveSkill}
          disabled={!isEditable}>
          <Delete />
        </IconButton>
      </TableCell>
      {openGenerateQuestionsModal &&
        isKnowledgeBlockCompetencyFieldsNonNullable(knowledgeBlock) &&
        isSkillFieldsNonNullable(skill) && (
          <GenerateQuestionsModal
            open={openGenerateQuestionsModal}
            onSuccess={refetchCompetencyMapGenQuestionsLog}
            onClose={() => setOpenGenerateQuestionsModal(false)}
            knowledgeBlock={knowledgeBlock}
            skill={skill}
          />
        )}
    </TableRow>
  );
};

export default Skill;
